import type { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  CheckboxGroupInput,
  Create,
  Edit,
  NumberInput,
  required,
  Resource,
  TextInput,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { Box, Divider, FormGroup, FormLabel } from "@mui/material";
import type { ExamLocation } from "../providers/examLocationsProvider";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Column } from "../misc/Column";
import { Row } from "../misc/Row";
import { examLocationWeekdays } from "../model/ExamLocation";
import { grants } from "../backoffice.access_control";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { DefaultList } from "../misc/DefaultList";
import { Form2 } from "../misc/Form2";
import { SaveButton2 } from "../buttons/SaveButton2";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateExamLocationForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        meta: { drivingSchoolId },
        onSuccess: () => {
          showSuccessToast("Prüfungsort erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/examLocations`);
        },
        onError: (error: Error) => {
          handleError("Fehler beim Erstellen des Prüfungsorts.", error);
        },
      }}
    >
      <ExamLocationForm mode="create" />
    </Create>
  );
}

function UpdateExamLocationForm() {
  // TODO: const examLocation = useRecordAndSwitchDrivingSchoolIfNecessary(...)
  // ... once the examLocationsProvider does not need meta.drivingSchoolId anymore
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  return (
    <Edit
      queryOptions={{ meta: { drivingSchoolId } }}
      mutationOptions={{
        meta: { drivingSchoolId },
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${drivingSchoolId}/examLocations`);
        },
        onError: (error: unknown) => {
          handleError("Fehler beim Speichern.", error);
        },
      }}
      mutationMode="pessimistic"
      title={<BackofficeRecordTitle header={<ExamLocationDetailsHeader />} />}
    >
      <ExamLocationForm mode="edit" />
    </Edit>
  );
}

function ExamLocationDetailsHeader() {
  const examLocation = useRecordContext<ExamLocation>();

  return (
    <RecordDetailsHeader
      backLabel="Prüfungsorte"
      backUrl={`/drivingSchools/${examLocation.drivingSchoolId}/examLocations`}
      title={examLocation.name}
    />
  );
}

function ExamLocationForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ gap: { xs: 2.5, sm: 3 }, maxWidth: "900px", padding: overviewCardStyle.padding, width: "100%" }}>
        <TextInput
          label="Name"
          source="name"
          autoFocus={mode === "create"}
          validate={required()}
          helperText={false}
          fullWidth
        />

        <Divider sx={{ width: "100%" }} />
        <FormGroup sx={{ gap: 2, minWidth: 0, width: "100%" }}>
          <FormLabel sx={{ fontWeight: 600 }}>Adresse</FormLabel>
          <Box
            sx={{
              alignItems: "start",
              display: "grid",
              gap: { xs: 2, sm: 3 },
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 200px minmax(0, 1fr)" },
              width: "100%",
            }}
          >
            <TextInput
              label="Straße"
              source="postalAddress.street"
              validate={required()}
              helperText={false}
              fullWidth
            />
            <TextInput
              label="PLZ"
              source="postalAddress.postalCode"
              validate={required()}
              helperText={false}
              fullWidth
            />
            <TextInput label="Ort" source="postalAddress.city" validate={required()} helperText={false} fullWidth />
          </Box>
        </FormGroup>

        <Divider sx={{ width: "100%" }} />
        <CheckboxGroupInput
          label="Prüfungsarten"
          source="types"
          choices={[
            { id: "practicalExam", name: "Praktische Prüfung" },
            { id: "theoryExam", name: "Theoretische Prüfung" },
          ]}
          validate={required()}
          helperText={false}
          sx={{
            width: "100%",
            "& .MuiCheckbox-root": {
              width: "auto",
            },
            "& .MuiFormGroup-root": {
              columnGap: 3,
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, max-content)" },
            },
          }}
        />

        <ExamTypeDependentFields />
        <ExamLocationFormActions mode={mode} />
      </Column>
    </Form2>
  );
}

function ExamTypeDependentFields() {
  const { control } = useFormContext<ExamLocation>();
  const types = useWatch({ name: "types", control }) || [];

  return (
    <>
      {types.includes("practicalExam") && (
        <ExamSettingsSection label="Praktische Prüfung">
          <NumberInput
            label="Praktische Prüfungstage im Voraus"
            source="practicalExamDaysInAdvance"
            validate={required()}
            helperText={false}
            fullWidth
          />
          <CheckboxGroupInput
            label="Praktische Prüfung Wochentags"
            source="practicalExamWeekdays"
            choices={examLocationWeekdaysOptions}
            helperText={false}
            sx={weekdayCheckboxGroupStyle}
          />
        </ExamSettingsSection>
      )}
      {types.includes("theoryExam") && (
        <ExamSettingsSection label="Theorieprüfung">
          <NumberInput
            label="Theorieprüfung Tage im Voraus"
            source="theoryExamDaysInAdvance"
            validate={required()}
            helperText={false}
            fullWidth
          />
          <CheckboxGroupInput
            label="Theorieprüfung Wochentags"
            source="theoryExamWeekdays"
            choices={examLocationWeekdaysOptions}
            helperText={false}
            sx={weekdayCheckboxGroupStyle}
          />
        </ExamSettingsSection>
      )}
    </>
  );
}

function ExamSettingsSection(props: { label: string; children: ReactNode }) {
  const { label, children } = props;
  return (
    <>
      <Divider sx={{ width: "100%" }} />
      <FormGroup sx={{ gap: 2, minWidth: 0, width: "100%" }}>
        <FormLabel sx={{ fontWeight: 600 }}>{label}</FormLabel>
        <Box
          sx={{
            alignItems: "start",
            display: "grid",
            gap: { xs: 2, md: 4 },
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(220px, 1fr) minmax(0, 2fr)" },
            width: "100%",
          }}
        >
          {children}
        </Box>
      </FormGroup>
    </>
  );
}

function ExamLocationFormActions(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canSave = grants.has(mode === "create" ? "ExamLocation:create" : "ExamLocation:edit");

  return (
    <Row sx={{ flexWrap: "wrap", gap: 1, justifyContent: "space-between", pt: 1 }}>
      {canSave && <SaveButton2 />}
      {mode === "edit" && grants.has("ExamLocation:delete") && (
        <DeleteRecordButton
          meta={{ drivingSchoolId }}
          onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/examLocations`}
        />
      )}
    </Row>
  );
}

const weekdayCheckboxGroupStyle = {
  my: 0,
  width: "100%",
  "& .MuiCheckbox-root": {
    width: "auto",
  },
  "& .MuiFormGroup-root": {
    columnGap: { xs: 1.5, sm: 2 },
    display: "grid",
    gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
    rowGap: 0.5,
  },
  "& .MuiFormControlLabel-root": {
    marginRight: 0,
    minWidth: 0,
  },
} as const;

export const examLocationsResource = (
  <Resource
    key="examLocations"
    name="examLocations"
    list={DefaultList}
    create={CreateExamLocationForm}
    edit={UpdateExamLocationForm}
    options={{
      label: "Prüfungsorte",
      createTitle: "Neuer Prüfungsort",
      noEntriesText: "Noch kein Prüfungsort hinterlegt.",
      noMatchingEntriesText: "Keine passenden Prüfungsorte gefunden.",
    }}
    recordRepresentation={(examLocation: ExamLocation) => examLocation.name}
  />
);

const examLocationWeekdaysOptions = Object.entries(examLocationWeekdays).map(([key, value]) => ({
  id: key,
  name: value,
}));
